<template>
  <div class="c-virtual-list c-virtual-list--grid">
    <div class="c-virtual-list__header">
      <slot name="headerSticky"></slot>
      <div :style="headerStyle"><slot name="header"></slot></div>
    </div>
    <div class="c-virtual-list__content" ref="content">
      <RecycleScroller
        :items="items"
        :item-size="itemSize"
        class="c-virtual-list__scroller"
        ref="scroller"
        @resize="resize"
      >
        <template slot="before">
          <slot name="before"></slot>
        </template>
        <template v-slot="{ item }">
          <slot :item="item"></slot>
        </template>
      </RecycleScroller>
      <div
        v-if="hasScrollbarX"
        class="c-virtual-list-scrollbar c-virtual-list-scrollbar--horizontal"
        :class="{ 'is-hover': isDraggingX }"
        :style="scrollbarXStyle"
        @mousedown.self="clickScrollbarX"
      >
        <div
          class="c-virtual-list-scrollbar__thumb"
          :style="thumbXStyle"
          @mousedown="mousedownScrollbarX"
        ></div>
      </div>
      <div
        v-if="hasScrollbarY"
        class="c-virtual-list-scrollbar c-virtual-list-scrollbar--vertical"
        :class="{ 'is-hover': isDraggingY }"
        @mousedown.self="clickScrollbarY"
      >
        <div
          class="c-virtual-list-scrollbar__thumb"
          :style="thumbYStyle"
          @mousedown="mousedownScrollbarY"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

export default {
  props: {
    items: {
      type: Array,
      required: true
    },
    itemSize: {
      type: Number,
      required: true
    },
    thumbMinSize: {
      type: Number,
      required: false,
      default: 20
    },
    thumbOffset: {
      type: Number,
      required: false,
      default: 3
    },
    scrollOffsetX: {
      type: Number,
      required: false,
      default: 0
    },
    cornerSize: {
      type: Number,
      required: false,
      default: 6
    }
  },
  components: {
    RecycleScroller
  },
  data() {
    return {
      offsetHeight: 0,
      offsetWidth: 0,
      scrollLeft: 0,
      scrollTop: 0,
      scrollHeight: 0,
      scrollWidth: 0,
      mousedownX: 0,
      mousedownY: 0,
      isDraggingX: false,
      isDraggingY: false
    };
  },
  computed: {
    headerStyle() {
      return {
        transform: 'translateX(' + -this.scrollLeft + 'px)'
      };
    },
    scrollMaxLeft() {
      return this.scrollWidth - this.offsetWidth;
    },
    scrollMaxTop() {
      return this.scrollHeight - this.offsetHeight;
    },
    thumbX() {
      const offsetLeft = this.thumbOffset;
      // 縦スクロールバーがある場合はオフセットを追加
      const offsetRight =
        this.thumbOffset + (this.hasScrollbarY ? this.cornerSize : 0);

      const maxSize = this.offsetWidth;
      const size = Math.round(
        Math.min(
          Math.max(maxSize * (maxSize / this.scrollWidth), this.thumbMinSize),
          maxSize
        )
      );
      const maxLeft = maxSize - size;

      return { offsetLeft, offsetRight, maxLeft, size };
    },
    thumbY() {
      const offsetTop = this.thumbOffset;
      // 横スクロールバーがある場合はオフセットを追加
      const offsetBottom =
        this.thumbOffset + (this.hasScrollbarX ? this.cornerSize : 0);

      const maxSize = this.offsetHeight;
      const size = Math.round(
        Math.min(
          Math.max(maxSize * (maxSize / this.scrollHeight), this.thumbMinSize),
          maxSize
        )
      );
      const maxTop = maxSize - size;

      return { offsetTop, offsetBottom, maxTop, size };
    },
    thumbXLeft() {
      const progress = this.scrollLeft / this.scrollMaxLeft;
      const left = Math.min(
        Math.max(this.thumbX.maxLeft * progress, 0),
        this.thumbX.maxLeft
      );
      return Math.round(left);
    },
    thumbYTop() {
      const progress = this.scrollTop / this.scrollMaxTop;
      const top = Math.min(
        Math.max(this.thumbY.maxTop * progress, 0),
        this.thumbY.maxTop
      );
      return Math.round(top);
    },
    thumbXStyle() {
      return {
        left: `${this.thumbXLeft}px`,
        'padding-left': `${this.thumbX.offsetLeft}px`,
        'padding-right': `${this.thumbX.offsetRight}px`,
        width: `${this.thumbX.size}px`
      };
    },
    thumbYStyle() {
      return {
        height: `${this.thumbY.size}px`,
        'padding-top': `${this.thumbY.offsetTop}px`,
        'padding-bottom': `${this.thumbY.offsetBottom}px`,
        top: `${this.thumbYTop}px`
      };
    },
    hasScrollbarX() {
      return this.scrollWidth > this.offsetWidth;
    },
    hasScrollbarY() {
      return this.scrollHeight > this.offsetHeight;
    },
    scrollbarXStyle() {
      return {
        left: `${this.scrollOffsetX}px`
      };
    }
  },
  watch: {
    items: {
      handler() {
        this.$nextTick(() => {
          this.resize();
        });
      }
    }
  },
  mounted() {
    this.$refs.scroller.$el.addEventListener('scroll', this.scroll);
  },
  methods: {
    clickScrollbarX(e) {
      // クリックした位置までスクロールさせる
      const pos = this.getScrollX(e.clientX - this.thumbX.size / 2);
      this.scrollToX(pos);
    },
    clickScrollbarY(e) {
      // クリックした位置までスクロールさせる
      const pos = this.getScrollY(e.clientY - this.thumbY.size / 2);
      this.scrollToY(pos);
    },
    getScrollX(clientPos) {
      let pos = clientPos - this.$refs.content.getBoundingClientRect().left;
      pos = Math.round(this.scrollWidth * (pos / this.offsetWidth));
      // リストのスクロール範囲を超えないようにする
      pos = Math.min(Math.max(pos, 0), this.scrollMaxLeft);
      return pos;
    },
    getScrollY(clientPos) {
      let pos = clientPos - this.$refs.content.getBoundingClientRect().top;
      pos = Math.round(this.scrollHeight * (pos / this.offsetHeight));
      // リストのスクロール範囲を超えないようにする
      pos = Math.min(Math.max(pos, 0), this.scrollMaxTop);
      return pos;
    },
    mousedownScrollbarX(e) {
      this.isDraggingX = true;
      this.mousedownX = e.offsetX;
      // スクロールバーのつまみにmousedownした時にドラッグで動かせるようにする
      // 負荷軽減のためこの時だけmousemoveとmouseupイベントを登録、スクローラーのスクロールイベントを一時的にやめる
      document.body.addEventListener('mousemove', this.mousemoveX);
      document.body.addEventListener('mouseup', this.mouseupX);
      this.$refs.scroller.$el.removeEventListener('scroll', this.scroll);
    },
    mousedownScrollbarY(e) {
      this.isDraggingY = true;
      this.mousedownY = e.offsetY;
      // スクロールバーのつまみにmousedownした時にドラッグで動かせるようにする
      // 負荷軽減のためこの時だけmousemoveとmouseupイベントを登録、スクローラーのスクロールイベントを一時的にやめる
      document.body.addEventListener('mousemove', this.mousemoveY);
      document.body.addEventListener('mouseup', this.mouseupY);
      this.$refs.scroller.$el.removeEventListener('scroll', this.scroll);
    },
    mousemoveX(e) {
      e.preventDefault();
      // スクロールバーの移動とリストの移動を連動させる
      const pos = this.getScrollX(
        e.clientX - this.mousedownX - this.scrollOffsetX
      );
      this.scrollToX(pos);
    },
    mousemoveY(e) {
      e.preventDefault();
      // スクロールバーの移動とリストの移動を連動させる
      const pos = this.getScrollY(e.clientY - this.mousedownY);
      this.scrollToY(pos);
    },
    mouseupX() {
      this.isDraggingX = false;
      document.body.removeEventListener('mousemove', this.mousemoveX);
      document.body.removeEventListener('mouseup', this.mouseupX);
      this.$refs.scroller.$el.addEventListener('scroll', this.scroll);
    },
    mouseupY() {
      this.isDraggingY = false;
      document.body.removeEventListener('mousemove', this.mousemoveY);
      document.body.removeEventListener('mouseup', this.mouseupY);
      this.$refs.scroller.$el.addEventListener('scroll', this.scroll);
    },
    resize() {
      const $el = this.$refs.scroller.$el;
      this.scrollLeft = $el.scrollLeft;
      this.scrollTop = $el.scrollTop;
      this.offsetHeight = $el.offsetHeight;
      this.offsetWidth = $el.offsetWidth - this.scrollOffsetX;
      this.scrollHeight = $el.scrollHeight;
      this.scrollWidth = $el.scrollWidth - this.scrollOffsetX;
    },
    scroll() {
      this.scrollLeft = this.$refs.scroller.$el.scrollLeft;
      this.scrollTop = this.$refs.scroller.$el.scrollTop;
    },
    scrollToX(scrollLeft) {
      this.scrollLeft = scrollLeft;
      this.$refs.scroller.$el.scrollLeft = scrollLeft;
      this.$refs.scroller.updateVisibleItems(false);
    },
    scrollToY(scrollTop) {
      this.scrollTop = scrollTop;
      this.$refs.scroller.$el.scrollTop = scrollTop;
      this.$refs.scroller.updateVisibleItems(false);
    },
    scrollToEnd() {
      this.$nextTick(() => {
        this.resize();
        this.scrollToX(this.scrollWidth);
      });
    }
  }
};
</script>
